import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { createHeadModel } from './head-model';
import { ZONES } from './zones';
import { initUI, toggleZone, clearSelection, getSelectedZoneIds, isZoneSelected, onSelectionCleared } from './ui';

// --- Scene ---
const container = document.getElementById('canvas-container')!;
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x0a0e1a);
scene.fog = new THREE.FogExp2(0x0a0e1a, 0.06);

// --- Camera ---
const camera = new THREE.PerspectiveCamera(
    45,
    window.innerWidth / window.innerHeight,
    0.1,
    100,
);
camera.position.set(0, 0.2, 4);

// --- Renderer ---
const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.1;
container.appendChild(renderer.domElement);

// --- Lighting (tuned for transparent/glass head) ---
scene.add(new THREE.AmbientLight(0x6688aa, 0.8));

const keyLight = new THREE.DirectionalLight(0xffffff, 1.2);
keyLight.position.set(2, 3, 4);
scene.add(keyLight);

const fillLight = new THREE.DirectionalLight(0x88bbff, 0.5);
fillLight.position.set(-3, 1, 2);
scene.add(fillLight);

const rimLight = new THREE.DirectionalLight(0xaaccff, 0.6);
rimLight.position.set(0, 2, -4);
scene.add(rimLight);

const bottomLight = new THREE.DirectionalLight(0x446688, 0.3);
bottomLight.position.set(0, -3, 0);
scene.add(bottomLight);

// --- Controls ---
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.08;
controls.enablePan = false;
controls.minDistance = 2.5;
controls.maxDistance = 7;
controls.minPolarAngle = Math.PI * 0.15;
controls.maxPolarAngle = Math.PI * 0.85;
controls.autoRotate = true;
controls.autoRotateSpeed = 0.5;
controls.touches = {
    ONE: THREE.TOUCH.ROTATE,
    TWO: THREE.TOUCH.DOLLY_ROTATE,
};

let autoRotateTimeout: ReturnType<typeof setTimeout>;
controls.addEventListener('start', () => {
    controls.autoRotate = false;
    clearTimeout(autoRotateTimeout);
});
controls.addEventListener('end', () => {
    clearTimeout(autoRotateTimeout);
    autoRotateTimeout = setTimeout(() => {
        controls.autoRotate = true;
    }, 5000);
});

// --- Zone hotspots ---
interface HotspotMesh extends THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial> {
    userData: { zoneId: string; zone: (typeof ZONES)[number] };
}

const hotspotMeshes: HotspotMesh[] = [];
const indicatorDots: THREE.Mesh<THREE.SphereGeometry, THREE.MeshBasicMaterial>[] = [];
const dotGeometry = new THREE.SphereGeometry(0.04, 12, 12);

// Symmetric zones: create hotspots on both sides
const MIRRORED_ZONES = new Set(['temples', 'jaw']);

function addHotspot(
    headGroup: THREE.Group,
    zone: (typeof ZONES)[number],
    x: number,
    y: number,
    z: number,
): void {
    const hotspotMat = new THREE.MeshBasicMaterial({
        color: 0xff4444,
        transparent: true,
        opacity: 0.0,
        depthTest: true,
        side: THREE.DoubleSide,
    });
    const hotspot = new THREE.Mesh(
        new THREE.SphereGeometry(zone.radius, 16, 16),
        hotspotMat,
    ) as HotspotMesh;
    hotspot.position.set(x, y, z);
    hotspot.userData = { zoneId: zone.id, zone };
    headGroup.add(hotspot);
    hotspotMeshes.push(hotspot);

    const dotMat = new THREE.MeshBasicMaterial({
        color: 0x66bbff,
        transparent: true,
        opacity: 0.6,
    });
    const dot = new THREE.Mesh(dotGeometry, dotMat);
    dot.position.set(x, y, z);
    headGroup.add(dot);
    indicatorDots.push(dot);
}

// --- Raycasting ---
const raycaster = new THREE.Raycaster();
const pointer = new THREE.Vector2();
let hoveredMesh: HotspotMesh | null = null;

function updatePointer(event: PointerEvent | TouchEvent): void {
    let x: number | undefined;
    let y: number | undefined;

    if ('clientX' in event) {
        x = event.clientX;
        y = event.clientY;
    } else if (event.touches?.length) {
        x = event.touches[0].clientX;
        y = event.touches[0].clientY;
    }

    if (x == null || y == null) return;
    pointer.x = (x / window.innerWidth) * 2 - 1;
    pointer.y = -(y / window.innerHeight) * 2 + 1;
}

function checkIntersections(): void {
    raycaster.setFromCamera(pointer, camera);
    const intersects = raycaster.intersectObjects(hotspotMeshes);

    if (hoveredMesh && (!intersects.length || intersects[0].object !== hoveredMesh)) {
        // Restore: if zone is selected keep highlight, otherwise hide
        if (isZoneSelected(hoveredMesh.userData.zoneId)) {
            hoveredMesh.material.opacity = 0.35;
            hoveredMesh.material.color.setHex(0xff2222);
        } else {
            hoveredMesh.material.opacity = 0.0;
            hoveredMesh.material.color.setHex(0xff4444);
        }
        hoveredMesh = null;
        renderer.domElement.style.cursor = 'grab';
    }

    if (intersects.length > 0) {
        const mesh = intersects[0].object as HotspotMesh;
        mesh.material.opacity = 0.25;
        hoveredMesh = mesh;
        renderer.domElement.style.cursor = 'pointer';
    }
}

// Desktop hover
renderer.domElement.addEventListener('pointermove', (e: PointerEvent) => {
    if (e.pointerType === 'touch') return;
    updatePointer(e);
    checkIntersections();
});

// Click / tap detection
let pointerDownPos: { x: number; y: number } | null = null;
let pointerDownTime = 0;
const TAP_THRESHOLD = 12;
const TAP_TIME = 350;

renderer.domElement.addEventListener('pointerdown', (e: PointerEvent) => {
    pointerDownPos = { x: e.clientX, y: e.clientY };
    pointerDownTime = Date.now();
    updatePointer(e);
});

renderer.domElement.addEventListener('pointerup', (e: PointerEvent) => {
    if (!pointerDownPos) return;
    const dx = e.clientX - pointerDownPos.x;
    const dy = e.clientY - pointerDownPos.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const elapsed = Date.now() - pointerDownTime;

    if (distance < TAP_THRESHOLD && elapsed < TAP_TIME) {
        updatePointer(e);
        handleTap();
    }
    pointerDownPos = null;
});

function handleTap(): void {
    raycaster.setFromCamera(pointer, camera);
    const intersects = raycaster.intersectObjects(hotspotMeshes);

    if (intersects.length > 0) {
        const mesh = intersects[0].object as HotspotMesh;
        const zone = mesh.userData.zone;
        const nowSelected = toggleZone(zone);

        // Update highlight for this zone's hotspot(s) — including mirrored copies
        hotspotMeshes.forEach((m) => {
            if (m.userData.zoneId === zone.id) {
                if (nowSelected) {
                    m.material.opacity = 0.35;
                    m.material.color.setHex(0xff2222);
                } else {
                    m.material.opacity = 0.0;
                    m.material.color.setHex(0xff4444);
                }
            }
        });
    } else if (getSelectedZoneIds().length > 0) {
        // Tap on empty space — clear everything
        clearSelection();
        hotspotMeshes.forEach((m) => {
            m.material.opacity = 0.0;
            m.material.color.setHex(0xff4444);
        });
    }
}

// --- Animation loop ---
const clock = new THREE.Clock();

function animate(): void {
    requestAnimationFrame(animate);
    controls.update();

    const time = clock.getElapsedTime();
    indicatorDots.forEach((dot, i) => {
        const phase = time * 1.5 + i * 0.7;
        const pulse = 0.3 + Math.sin(phase) * 0.25;
        dot.material.opacity = pulse;
        const scale = 1.0 + Math.sin(phase) * 0.2;
        dot.scale.setScalar(scale);
    });

    renderer.render(scene, camera);
}

// --- Resize ---
window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
});

// --- Init (async for OBJ model loading) ---
async function init(): Promise<void> {
    // Start UI immediately
    initUI(ZONES);

    // When selection is cleared from UI (close btn, Escape, whole-head), reset 3D highlights
    onSelectionCleared(() => {
        hotspotMeshes.forEach((m) => {
            m.material.opacity = 0.0;
            m.material.color.setHex(0xff4444);
        });
    });

    // Start animation loop (will render empty scene while model loads)
    animate();

    // Load the 3D head model
    const headGroup = await createHeadModel();
    scene.add(headGroup);

    // Hide loading overlay
    const loadingOverlay = document.getElementById('loading-overlay');
    if (loadingOverlay) {
        loadingOverlay.classList.add('hidden');
        setTimeout(() => loadingOverlay.remove(), 600);
    }

    // Add zone hotspots to the head group
    ZONES.forEach((zone) => {
        if (zone.id === 'whole_head') return;

        const { x, y, z } = zone.position;
        addHotspot(headGroup, zone, x, y, z);

        // Mirror symmetric zones to the other side
        if (MIRRORED_ZONES.has(zone.id) && x !== 0) {
            addHotspot(headGroup, zone, -x, y, z);
        }
    });
}

init();
